// upiInstructionBoard.js
// Generates the Amik "UPI Charging Instruction Board" — a downloadable A4 landscape
// page per charger connector — from the design handoff (project "EV Charger Payment
// Instructions", HANDOFF.md). The connector's UPI QR + charger details are filled
// into the template, rendered off-screen, rasterized with html2canvas and saved as
// an A4 landscape PDF (no print dialog). The connector price/tariff block can be
// included or omitted ("with price" / "without price").
//
// Design is reproduced faithfully: Amik colour tokens, Poppins/Inter, the signature
// gradient, QR ~310px (anti-tamper), the payee check ("Amik Technologies"), the
// Charger ID pill, refund Q&A, and the info@amiktech.in / www.amiktech.in footer.

// jsPDF + html2canvas are loaded lazily (only when a board is actually
// downloaded) so they stay out of the charger form's initial bundle.

// --- Amik colour tokens (subset used by the board) -------------------------------
const TOKENS_CSS = `
:root{
  --amik-blue:#1488D6; --amik-teal:#1FA98C; --amik-green:#4CB749;
  --gradient-signature:linear-gradient(118deg,#1488D6 0%,#1FA98C 52%,#4CB749 100%);
  --ink:#0F1A2B; --ink-2:#3C4D5E; --muted:#6E7D8B;
  --line:#E6EAEE; --line-strong:#D2D9E0;
  --success-soft:#EAF3E0; --surface-sunken:#EEF1F4;
}`;

// Amik wordmark logos (inlined from the design project's assets).
const LOGO_PRIMARY = `<svg xmlns="http://www.w3.org/2000/svg" width="580" height="192" viewBox="0 0 232 96"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#1488D6"></stop><stop offset="0.55" stop-color="#1FA98C"></stop><stop offset="1" stop-color="#4CB749"></stop></linearGradient></defs><g fill="none" stroke="url(#g)" stroke-linecap="round" stroke-linejoin="round"><path d="M22 68 L48 46 L74 68" stroke-width="13"></path><path d="M30 53 L48 37 L66 53" stroke-width="11"></path><path d="M38 39 L48 29 L58 39" stroke-width="9"></path></g><text x="88" y="65" font-family="'Poppins','Helvetica Neue',Arial,sans-serif" font-weight="700" font-size="56" letter-spacing="-1.5" fill="#0F1A2B">amik</text></svg>`;
const LOGO_WHITE = `<svg xmlns="http://www.w3.org/2000/svg" width="580" height="192" viewBox="0 0 232 96"><g fill="none" stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"><path d="M22 68 L48 46 L74 68" stroke-width="13"></path><path d="M30 53 L48 37 L66 53" stroke-width="11"></path><path d="M38 39 L48 29 L58 39" stroke-width="9"></path></g><text x="88" y="65" font-family="'Poppins','Helvetica Neue',Arial,sans-serif" font-weight="700" font-size="56" letter-spacing="-1.5" fill="#FFFFFF">amik</text></svg>`;

const svgDataUri = (svg) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

// Official UPI-app logos (supplied by the client) inlined as SVG data-URI <img>s
// so the board stays self-contained. width/height are set explicitly so
// html2canvas scales them correctly.
const LOGO_GPAY = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="#FFFFFF"></circle><g transform="translate(9,9) scale(0.625)"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path></g></svg>`;
const LOGO_PHONEPE = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><rect width="48" height="48" rx="11" fill="#5F259F"></rect><text x="24" y="31" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="#FFFFFF">Pe</text></svg>`;
const LOGO_PAYTM = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="40" viewBox="0 0 120 40"><text x="0" y="29" font-family="Arial, sans-serif" font-size="28" font-weight="bold" letter-spacing="-0.5"><tspan fill="#002E6E">pay</tspan><tspan fill="#00BAF2">tm</tspan></text></svg>`;
const LOGO_UPI = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="#0F1A2B"></circle><path d="M27 12l6 12-6 12" fill="none" stroke="#F5A623" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"></path><path d="M20 12l6 12-6 12" fill="none" stroke="#4CB749" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"></path></svg>`;

// Languages the board can be printed in. `font` overrides the board font so the
// script renders (null keeps the default Poppins/Inter); `googleFont` is the
// Google Fonts family query to load. Add a language by adding an entry here plus a
// matching BOARD_STRINGS dictionary.
export const BOARD_LANGUAGES = [
  { code: "en", native: "English", font: null, googleFont: null },
  {
    code: "hi",
    native: "हिन्दी",
    font: "'Noto Sans Devanagari','Poppins',sans-serif",
    googleFont: "Noto+Sans+Devanagari:wght@400;500;600;700",
  },
];

// Translations for every piece of text on the board. Values are trusted HTML
// (developer-authored) — <b> and entities are allowed. Latin technical terms
// (amiktech.in, UPI, GST, phone number, Charger ID) are intentionally kept as-is.
const BOARD_STRINGS = {
  en: {
    headTitle: "EV Charging — Pay via UPI",
    headSub: "No app required · Scan, pay &amp; charge",
    scanPay: "Scan &amp; Pay via UPI",
    connector: "Connector",
    needId: "You'll need this ID to check charger &amp; transaction status",
    tariff: "Tariff",
    perKwh: "per kWh",
    perHour: "per hour",
    perMin: "per min",
    plusGst: "+ GST",
    howToStart: "How to start charging",
    step1Title: "Plug the gun into your vehicle",
    step1Desc:
      "Seat the connector firmly until it locks into the charging port.",
    step2Title: "Scan the QR &amp; make the payment",
    step2Desc: "Pay with any UPI app:",
    andMore: "&amp; more",
    step3Title: "Charging starts automatically",
    step3Desc:
      "Charging begins once the payment is confirmed. Stay nearby until it starts.",
    whatsapp: "WhatsApp support",
    goodToKnow: "Good to know",
    q1Title: "Refund — didn't charge or unused amount?",
    q1Body:
      "Your money is refunded automatically to your bank account — usually immediately, occasionally up to 30 minutes. To check your refund receipt, go to <b>amiktech.in</b> → <b>Charger Status</b> and enter your <b>UPI transaction ID</b>, or upload your <b>UPI payment receipt</b>.",
    q2Title: "How do I find the exact issue?",
    q2Body:
      "Use the <b>Ask Us Anything</b> chat at the bottom of <b>www.amiktech.in</b> — it can tell you exactly why your charging session didn't start.",
    q3Title: "How do I check charger &amp; transaction status?",
    q3Body:
      "Go to <b>amiktech.in</b> → <b>More</b> → <b>Charger Status</b> and enter the Charger ID to view the charger and your transactions.",
    q4Title: "How much should I pay?",
    q4Body:
      "Pay any amount you like — the charger delivers energy worth the amount paid and stops automatically.",
    q5Title: "Payment failed or deducted twice?",
    q5Body:
      "Failed UPI payments are auto-reversed by your bank. Keep your UPI transaction ID and Charger ID handy when contacting support.",
    support: "Support:",
  },
  hi: {
    headTitle: "ईवी चार्जिंग — UPI से भुगतान करें",
    headSub: "कोई ऐप ज़रूरी नहीं · स्कैन करें, भुगतान करें और चार्ज करें",
    scanPay: "स्कैन करें और UPI से भुगतान करें",
    connector: "कनेक्टर",
    needId: "चार्जर और ट्रांज़ैक्शन की स्थिति देखने के लिए यह ID ज़रूरी है",
    tariff: "टैरिफ़",
    perKwh: "प्रति kWh",
    perHour: "प्रति घंटा",
    perMin: "प्रति मिनट",
    plusGst: "+ GST",
    howToStart: "चार्जिंग कैसे शुरू करें",
    step1Title: "गन को अपने वाहन में लगाएँ",
    step1Desc:
      "कनेक्टर को चार्जिंग पोर्ट में तब तक मज़बूती से लगाएँ जब तक वह लॉक न हो जाए।",
    step2Title: "QR स्कैन करें और भुगतान करें",
    step2Desc: "किसी भी UPI ऐप से भुगतान करें:",
    andMore: "और भी",
    step3Title: "चार्जिंग अपने आप शुरू हो जाती है",
    step3Desc:
      "भुगतान की पुष्टि होते ही चार्जिंग शुरू हो जाती है। शुरू होने तक पास रहें।",
    whatsapp: "WhatsApp सहायता",
    goodToKnow: "ध्यान देने योग्य बातें",
    q1Title: "रिफ़ंड — चार्ज नहीं हुआ या राशि बची?",
    q1Body:
      "आपका पैसा अपने आप आपके बैंक खाते में वापस आ जाता है — आमतौर पर तुरंत, कभी-कभी 30 मिनट तक। रिफ़ंड रसीद देखने के लिए <b>amiktech.in</b> → <b>Charger Status</b> पर जाएँ और अपना <b>UPI ट्रांज़ैक्शन ID</b> डालें, या अपनी <b>UPI भुगतान रसीद</b> अपलोड करें।",
    q2Title: "सही समस्या कैसे पता करें?",
    q2Body:
      "<b>www.amiktech.in</b> के नीचे दिए गए <b>Ask Us Anything</b> चैट का उपयोग करें — यह बता सकता है कि आपका चार्जिंग सत्र क्यों शुरू नहीं हुआ।",
    q3Title: "चार्जर और ट्रांज़ैक्शन की स्थिति कैसे देखें?",
    q3Body:
      "<b>amiktech.in</b> → <b>More</b> → <b>Charger Status</b> पर जाएँ और चार्जर व अपने ट्रांज़ैक्शन देखने के लिए Charger ID डालें।",
    q4Title: "मुझे कितना भुगतान करना चाहिए?",
    q4Body:
      "आप जितनी चाहें राशि का भुगतान करें — चार्जर भुगतान की गई राशि के बराबर ऊर्जा देता है और अपने आप बंद हो जाता है।",
    q5Title: "भुगतान विफल हुआ या दो बार कट गया?",
    q5Body:
      "विफल UPI भुगतान आपके बैंक द्वारा अपने आप वापस कर दिए जाते हैं। सहायता से संपर्क करते समय अपना UPI ट्रांज़ैक्शन ID और Charger ID साथ रखें।",
    support: "सहायता:",
  },
};

// The instruction board page markup (from UPI Instruction Board.dc.html), with
// {{...}} props to fill. Text uses {{t_*}} placeholders filled from BOARD_STRINGS.
// Rendered as a single fixed A4 landscape page.
const PAGE_HTML = `
<section class="page" style="display:flex;flex-direction:column;position:relative;padding-bottom:74px;box-sizing:border-box;background:#FFFFFF;font-family:'Inter',sans-serif;color:var(--ink)">
  <div style="flex:none;height:10px;background:var(--gradient-signature)"></div>
  <header style="display:flex;align-items:center;justify-content:space-between;padding:18px 44px 14px;border-bottom:1px solid var(--line)">
    <img src="{{logoPrimary}}" alt="Amik Tech" style="height:42px">
    <div style="text-align:right">
      <div style="font-family:'Poppins',sans-serif;font-weight:700;font-size:23px;letter-spacing:-0.01em">{{t_headTitle}}</div>
      <div style="font-size:14px;color:var(--muted)">{{t_headSub}}</div>
    </div>
  </header>
  <div style="display:grid;grid-template-columns:300px 1fr 1.05fr;gap:22px;padding:16px 44px 20px;align-items:stretch;flex:1">
    <div style="display:flex;flex-direction:column;justify-content:flex-start;gap:14px;height:100%">
      <div style="border:1.5px solid var(--line-strong);border-radius:14px;padding:14px;background:#FFFFFF;text-align:center">
        <div style="font-family:'Poppins',sans-serif;font-weight:700;font-size:16px;color:var(--amik-green)">{{t_scanPay}}</div>
        <img src="{{qrSrc}}" alt="UPI payment QR code" style="width:250px;height:250px;display:block;margin:10px auto 8px">
        <div style="font-family:'Poppins',sans-serif;font-weight:700;font-size:15px;color:var(--ink);line-height:1.3">{{chargerName}}</div>
      </div>
      <div style="display:flex;flex-direction:column;gap:9px">
        <img src="{{chargerIdBadge}}" alt="Charger ID {{chargerId}}" style="width:100%;display:block">
        {{connectorIdBadge}}
        <div style="font-size:11.5px;color:var(--muted);text-align:center;line-height:1.4">{{t_needId}}</div>
        {{tariffBlock}}
      </div>
    </div>
    <div style="display:flex;flex-direction:column;justify-content:space-between;gap:9px;height:100%">
      <div style="font-family:'Poppins',sans-serif;font-weight:700;font-size:22px;letter-spacing:-0.01em;line-height:1.15">{{t_howToStart}}</div>
      <div style="display:flex;flex-direction:column;gap:16px;flex:1">
        <div style="display:flex;gap:14px;align-items:center;border:1px solid var(--line);border-radius:14px;padding:14px 18px;flex:1 1 auto">
          <img src="{{stepCircle1}}" alt="Step 1" style="flex:none;width:42px;height:42px;display:block">
          <div>
            <div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:18px">{{t_step1Title}}</div>
            <div style="font-size:15px;color:var(--ink-2);margin-top:3px">{{t_step1Desc}}</div>
          </div>
        </div>
        <div style="display:flex;gap:14px;align-items:center;border:1px solid var(--line);border-radius:14px;padding:14px 18px;flex:1 1 auto">
          <img src="{{stepCircle2}}" alt="Step 2" style="flex:none;width:42px;height:42px;display:block">
          <div>
            <div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:18px">{{t_step2Title}}</div>
            <div style="font-size:15px;color:var(--ink-2);margin-top:3px">{{t_step2Desc}}</div>
            <div style="display:flex;align-items:center;gap:8px;margin-top:8px;flex-wrap:wrap">
              <img src="{{logoGpay}}" alt="Google Pay" style="height:30px;display:block">
              <img src="{{logoPhonepe}}" alt="PhonePe" style="height:30px;display:block">
              <img src="{{logoPaytm}}" alt="Paytm" style="height:26px;display:block">
              <img src="{{logoUpi}}" alt="UPI" style="height:30px;display:block">
              <span style="font-size:14px;color:var(--muted)">{{t_andMore}}</span>
            </div>
          </div>
        </div>
        <div style="display:flex;gap:14px;align-items:center;border:1px solid var(--line);border-radius:14px;padding:14px 18px;flex:1 1 auto">
          <img src="{{stepCircle3}}" alt="Step 3" style="flex:none;width:42px;height:42px;display:block">
          <div>
            <div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:18px">{{t_step3Title}}</div>
            <div style="font-size:15px;color:var(--ink-2);margin-top:3px">{{t_step3Desc}}</div>
          </div>
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;border:1.5px solid var(--line-strong);border-radius:10px;padding:9px 13px;margin-top:2px">
        <span style="font-family:'Poppins',sans-serif;font-weight:600;font-size:13px;color:var(--amik-green)">{{t_whatsapp}}</span>
        <span style="font-family:'Poppins',sans-serif;font-weight:700;font-size:16px;color:var(--ink)">+91 88263 62480</span>
      </div>
    </div>
    <div style="display:flex;flex-direction:column;gap:12px;height:100%">
      <div style="font-family:'Poppins',sans-serif;font-weight:700;font-size:21px;line-height:1.1">{{t_goodToKnow}}</div>
      <div style="display:flex;flex-direction:column;gap:10px;flex:1;justify-content:space-between">
      <div style="background:var(--surface-sunken);border-radius:11px;padding:10px 14px"><div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:14px">{{t_q1Title}}</div><div style="font-size:12.5px;color:var(--ink-2);line-height:1.4;margin-top:2px">{{t_q1Body}}</div></div>
      <div style="background:var(--surface-sunken);border-radius:11px;padding:10px 14px"><div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:14px">{{t_q2Title}}</div><div style="font-size:12.5px;color:var(--ink-2);line-height:1.4;margin-top:2px">{{t_q2Body}}</div></div>
      <div style="background:var(--surface-sunken);border-radius:11px;padding:10px 14px"><div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:14px">{{t_q3Title}}</div><div style="font-size:12.5px;color:var(--ink-2);line-height:1.4;margin-top:2px">{{t_q3Body}}</div></div>
      <div style="background:var(--surface-sunken);border-radius:11px;padding:10px 14px"><div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:14px">{{t_q4Title}}</div><div style="font-size:12.5px;color:var(--ink-2);line-height:1.4;margin-top:2px">{{t_q4Body}}</div></div>
      <div style="background:var(--surface-sunken);border-radius:11px;padding:10px 14px"><div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:14px">{{t_q5Title}}</div><div style="font-size:12.5px;color:var(--ink-2);line-height:1.4;margin-top:2px">{{t_q5Body}}</div></div>
      </div>
    </div>
  </div>
  <footer style="position:absolute;left:0;right:0;bottom:0;height:58px;background:var(--ink);color:#FFFFFF;display:flex;align-items:center;justify-content:space-between;gap:18px;padding:0 44px">
    <img src="{{logoWhite}}" alt="Amik Tech" style="height:28px">
    <div style="font-size:14px;color:#AAB4BE">{{chargerName}}{{footerConnector}} · {{t_support}} info@amiktech.in</div>
    <div style="font-family:'Poppins',sans-serif;font-weight:600;font-size:16px">www.amiktech.in</div>
  </footer>
</section>`;

const escapeHtml = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fillAll = (tpl, key, val) => tpl.split(`{{${key}}}`).join(val);

// Connector numbers on the board read as station labels, so show them zero-padded
// to two digits (1 -> "01", 2 -> "02"). Non-numeric ids pass through unchanged.
const formatConnectorId = (v) => {
  const s = String(v ?? "").trim();
  if (s === "") return "—";
  return /^\d+$/.test(s) ? s.padStart(2, "0") : s;
};

// html2canvas draws HTML text at the baseline (ignoring line-height/flex vertical
// centering), so small centered labels render bottom-aligned. Rendering them as an
// SVG <img> with dominant-baseline="central" centers reliably (the browser
// rasterizes the SVG itself). Used for the numbered step badges and the Charger ID.
const stepCircleSvg = (n) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42"><defs><linearGradient id="sc" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#1488D6"/><stop offset="0.52" stop-color="#1FA98C"/><stop offset="1" stop-color="#4CB749"/></linearGradient></defs><circle cx="21" cy="21" r="21" fill="url(#sc)"/><text x="21" y="22" text-anchor="middle" dominant-baseline="central" font-family="Arial,Helvetica,sans-serif" font-weight="700" font-size="20" fill="#FFFFFF">${n}</text></svg>`;

// A gradient "label | value" pill drawn as an SVG (rasterized as an <img>), so the
// text is vertically centered via dominant-baseline="central" instead of falling to
// the baseline the way html2canvas renders centered HTML text. Used for both the
// Charger ID and the Connector ID so they match. `gradId` keeps the two SVGs'
// gradient defs from colliding.
const idPillSvg = (gradId, label, value) => {
  const W = 300;
  const H = 48;
  const boxX = 100;
  const boxW = W - boxX - 14;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><defs><linearGradient id="${gradId}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#1488D6"/><stop offset="0.52" stop-color="#1FA98C"/><stop offset="1" stop-color="#4CB749"/></linearGradient></defs><rect width="${W}" height="${H}" rx="10" fill="url(#${gradId})"/><text x="16" y="${H / 2 + 1}" text-anchor="start" dominant-baseline="central" font-family="Arial,Helvetica,sans-serif" font-weight="600" font-size="13" fill="#FFFFFF">${escapeHtml(label)}</text><rect x="${boxX}" y="9" width="${boxW}" height="${H - 18}" rx="6" fill="#0F1A2B" fill-opacity="0.28"/><text x="${boxX + boxW / 2}" y="${H / 2 + 1}" text-anchor="middle" dominant-baseline="central" font-family="Arial,Helvetica,sans-serif" font-weight="700" font-size="18" letter-spacing="1" fill="#FFFFFF">${escapeHtml(value)}</text></svg>`;
};

const chargerIdSvg = (id) => idPillSvg("cid", "Charger ID", id);
const connectorIdSvg = (label, id) => idPillSvg("cnid", label, id);

// The connector price/tariff box. Included only when downloading "with price".
const TARIFF_BLOCK = `<div style="text-align:center;border:1.5px solid var(--line-strong);border-radius:10px;padding:11px 14px">
        <span style="font-family:'Poppins',sans-serif;font-weight:500;font-size:14px;color:var(--ink-2)">{{t_tariff}}</span>
        <span style="font-family:'Poppins',sans-serif;font-weight:700;font-size:22px;color:var(--ink);margin:0 6px">₹ {{tariff}}</span>
        <span style="font-size:14px;color:var(--muted)">{{t_tariffUnit}} {{t_plusGst}}</span>
      </div>`;

/**
 * Serialize the react-qr-code <svg> inside a container element into a PNG data
 * URL (white background), so it can be embedded in the print document.
 */
export const getQrDataUrlFromContainer = (containerId, size = 640) =>
  new Promise((resolve, reject) => {
    const container = document.getElementById(containerId);
    const svg = container && container.querySelector("svg");
    if (!svg) {
      reject(new Error("QR code not found"));
      return;
    }
    const clone = svg.cloneNode(true);
    clone.setAttribute("width", size);
    clone.setAttribute("height", size);
    const svgStr = new XMLSerializer().serializeToString(clone);
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      try {
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Failed to rasterize QR"));
    img.src = url;
  });

// Build the filled <section class="page"> markup. When `showPrice` is false the
// connector tariff box is omitted ("download without price").
const buildBoardPage = (
  {
    qrDataUrl,
    chargerName,
    chargerId,
    connectorId,
    showConnectorId = true,
    tariff,
    tariffUnitKey,
  },
  showPrice,
  langCode = "en"
) => {
  const strings = BOARD_STRINGS[langCode] || BOARD_STRINGS.en;
  let page = PAGE_HTML;
  page = fillAll(page, "tariffBlock", showPrice ? TARIFF_BLOCK : "");
  // Translated text ({{t_*}} placeholders).
  Object.keys(strings).forEach((k) => {
    page = fillAll(page, `t_${k}`, strings[k]);
  });
  const unitKey = tariffUnitKey || "perKwh";
  page = fillAll(page, "t_tariffUnit", strings[unitKey] || strings.perKwh);
  page = fillAll(page, "logoPrimary", svgDataUri(LOGO_PRIMARY));
  page = fillAll(page, "logoWhite", svgDataUri(LOGO_WHITE));
  page = fillAll(page, "logoGpay", svgDataUri(LOGO_GPAY));
  page = fillAll(page, "logoPhonepe", svgDataUri(LOGO_PHONEPE));
  page = fillAll(page, "logoPaytm", svgDataUri(LOGO_PAYTM));
  page = fillAll(page, "logoUpi", svgDataUri(LOGO_UPI));
  page = fillAll(page, "stepCircle1", svgDataUri(stepCircleSvg(1)));
  page = fillAll(page, "stepCircle2", svgDataUri(stepCircleSvg(2)));
  page = fillAll(page, "stepCircle3", svgDataUri(stepCircleSvg(3)));
  page = fillAll(
    page,
    "chargerIdBadge",
    svgDataUri(chargerIdSvg(chargerId || "—"))
  );
  // Single-connector chargers don't need a connector label, so the badge and the
  // footer's connector segment are dropped in that case.
  const connectorLabel = formatConnectorId(connectorId);
  page = fillAll(
    page,
    "connectorIdBadge",
    showConnectorId
      ? `<img src="${svgDataUri(
          connectorIdSvg(strings.connector, connectorLabel)
        )}" alt="${escapeHtml(strings.connector)} ${escapeHtml(
          connectorLabel
        )}" style="width:100%;display:block">`
      : ""
  );
  page = fillAll(
    page,
    "footerConnector",
    showConnectorId ? ` · ${strings.connector} ${escapeHtml(connectorLabel)}` : ""
  );
  page = fillAll(page, "qrSrc", qrDataUrl);
  page = fillAll(page, "chargerName", escapeHtml(chargerName || "EV Charger"));
  page = fillAll(page, "chargerId", escapeHtml(chargerId || "—"));
  // Keep a real "0" tariff (free/zero-rate connector); only fall back to the
  // placeholder when the tariff is genuinely absent.
  const hasTariff =
    tariff !== null && tariff !== undefined && `${tariff}`.trim() !== "";
  page = fillAll(page, "tariff", escapeHtml(hasTariff ? tariff : "—.—"));
  return page;
};

// A4 landscape at 96dpi (px). The .page is sized in px here (not mm) so it can be
// rendered off-screen and rasterized at a fixed, print-accurate aspect ratio.
const A4_LANDSCAPE_W = 1123;
const A4_LANDSCAPE_H = 794;

// Load Poppins/Inter plus the Noto font for each requested language, and wait for
// them so html2canvas rasterizes with the correct (script-supporting) glyphs.
const loadBoardFonts = async (langCodes = ["en"]) => {
  if (typeof document === "undefined" || !document.fonts) return;

  const families = [
    "Poppins:wght@400;500;600;700",
    "Inter:wght@400;500;600",
  ];
  const checks = ["700 24px Poppins", "500 14px Inter"];
  langCodes.forEach((code) => {
    const lang = BOARD_LANGUAGES.find((l) => l.code === code);
    if (lang && lang.googleFont) {
      if (!families.includes(lang.googleFont)) families.push(lang.googleFont);
      const fam = lang.font.split(",")[0].replace(/'/g, "");
      checks.push(`600 16px '${fam}'`);
    }
  });

  const href = `https://fonts.googleapis.com/css2?${families
    .map((f) => `family=${f}`)
    .join("&")}&display=swap`;

  let link = document.getElementById("amik-upi-board-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "amik-upi-board-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  const changed = link.href !== href;
  link.href = href;
  // Give a newly-added stylesheet a moment to register its @font-face rules
  // before asking the FontFaceSet to load them.
  if (changed) await new Promise((r) => setTimeout(r, 250));

  try {
    await Promise.all(checks.map((c) => document.fonts.load(c)));
    await document.fonts.ready;
  } catch (e) {
    /* fall back to system fonts if a webfont fails to load */
  }
};

// Render one language's board off-screen and rasterize it to a canvas.
const renderBoardCanvas = async (props, showPrice, lang, html2canvas) => {
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = `position:fixed;left:-100000px;top:0;width:${A4_LANDSCAPE_W}px;height:${A4_LANDSCAPE_H}px;background:#FFFFFF;overflow:hidden;z-index:-1;`;

  // For non-Latin scripts, force the script-supporting font across the board.
  const fontOverride = lang.font
    ? `.amik-upi-board .page, .amik-upi-board .page *{ font-family:${lang.font} !important; }`
    : "";
  const style = document.createElement("style");
  style.textContent = `${TOKENS_CSS}
    .amik-upi-board .page{ width:${A4_LANDSCAPE_W}px; height:${A4_LANDSCAPE_H}px; margin:0; overflow:hidden; box-shadow:none; }
    .amik-upi-board *{ box-sizing:border-box; }
    ${fontOverride}`;
  host.className = "amik-upi-board";
  host.appendChild(style);
  host.insertAdjacentHTML("beforeend", buildBoardPage(props, showPrice, lang.code));
  document.body.appendChild(host);

  try {
    const pageEl = host.querySelector(".page");
    return await html2canvas(pageEl, {
      scale: 2,
      backgroundColor: "#FFFFFF",
      useCORS: true,
      logging: false,
      width: A4_LANDSCAPE_W,
      height: A4_LANDSCAPE_H,
      windowWidth: A4_LANDSCAPE_W,
      windowHeight: A4_LANDSCAPE_H,
    });
  } finally {
    document.body.removeChild(host);
  }
};

/**
 * Render the connector's A4 landscape board and save it as a PDF download (no
 * print dialog). One page per selected language.
 * props: { qrDataUrl, chargerName, chargerId, connectorId, tariff, tariffUnitKey }.
 * options: { showPrice = true, languages = ["en"], fileName }.
 */
export const downloadUpiInstructionBoard = async (
  props,
  { showPrice = true, languages = ["en"], fileName } = {}
) => {
  const codes =
    Array.isArray(languages) && languages.length ? languages : ["en"];

  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
    loadBoardFonts(codes),
  ]);

  let pdf = null;
  for (let i = 0; i < codes.length; i += 1) {
    const lang =
      BOARD_LANGUAGES.find((l) => l.code === codes[i]) || BOARD_LANGUAGES[0];
    // eslint-disable-next-line no-await-in-loop
    const canvas = await renderBoardCanvas(props, showPrice, lang, html2canvas);
    // PNG (lossless) so the embedded UPI payment QR stays crisp and scannable.
    const imgData = canvas.toDataURL("image/png");
    if (!pdf) {
      pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    } else {
      pdf.addPage("a4", "landscape");
    }
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, "PNG", 0, 0, pw, ph);
  }

  const safeId = String(props.chargerId || "charger").replace(
    /[^a-zA-Z0-9._-]+/g,
    "-"
  );
  const suffix = showPrice ? "with-price" : "without-price";
  pdf.save(fileName || `UPI-Instruction-Board-${safeId}-${suffix}.pdf`);
};
